import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import attendanceRegularizationService from '../../../services/attendanceRegularizationService';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';

const AttendanceRegularization = ({ isAdmin, userProfile, clientId }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    date_from: '',
    date_to: ''
  });

  const [formData, setFormData] = useState({
    attendance_date: '',
    request_type: 'correction',
    requested_clock_in: '',
    requested_clock_out: '',
    requested_break_duration_minutes: 0,
    reason: '',
    employee_id: ''
  });

  useEffect(() => {
    loadRequests();
  }, [filters, clientId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const requestFilters = { ...filters };
      if (clientId) {
        requestFilters.client_id = clientId;
      }
      const result = await attendanceRegularizationService.getRegularizationRequests(requestFilters);
      if (result?.success) {
        setRequests(result.data || []);
      }
    } catch (error) {
      setNotification({ message: 'Failed to load requests', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!formData.attendance_date || !formData.reason) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const result = await attendanceRegularizationService.createRegularizationRequest(formData);
      if (result?.success) {
        setNotification({ message: 'Request created successfully', type: 'success' });
        setShowCreateModal(false);
        setFormData({
          attendance_date: '',
          request_type: 'correction',
          requested_clock_in: '',
          requested_clock_out: '',
          requested_break_duration_minutes: 0,
          reason: '',
          employee_id: ''
        });
        loadRequests();
      } else {
        setNotification({ message: result?.error || 'Failed to create request', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setLoading(true);
      const result = await attendanceRegularizationService.approveRequest(requestId);
      if (result?.success) {
        setNotification({ message: 'Request approved successfully', type: 'success' });
        loadRequests();
      } else {
        setNotification({ message: result?.error || 'Failed to approve request', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId, reason) => {
    if (!reason || reason.trim() === '') {
      setNotification({ message: 'Please provide a rejection reason', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const result = await attendanceRegularizationService.rejectRequest(requestId, reason);
      if (result?.success) {
        setNotification({ message: 'Request rejected', type: 'success' });
        loadRequests();
      } else {
        setNotification({ message: result?.error || 'Failed to reject request', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Attendance Regularization</h4>
          <p className="text-sm text-muted-foreground">Request corrections or manual entries</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Icon name="Plus" size={16} className="mr-2" />
          Create Request
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select
          value={filters.status}
          onValueChange={(value) => setFilters({ ...filters, status: value })}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' }
          ]}
        />
        <Input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          placeholder="From Date"
        />
        <Input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          placeholder="To Date"
        />
        <Button variant="outline" onClick={loadRequests}>
          <Icon name="Search" size={16} className="mr-2" />
          Filter
        </Button>
      </div>

      {/* Requests Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-2">Loading...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
            <p>No regularization requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Clock In</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Clock Out</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">
                      {new Date(request.attendance_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {request.full_name || request.employee_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {request.request_type?.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {request.requested_clock_in || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {request.requested_clock_out || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                      {request.reason}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    {isAdmin && request.status === 'pending' && (
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprove(request.id)}
                          >
                            <Icon name="Check" size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) handleReject(request.id, reason);
                            }}
                          >
                            <Icon name="X" size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-lg font-semibold text-foreground">Create Regularization Request</h5>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Attendance Date *
                </label>
                <Input
                  type="date"
                  value={formData.attendance_date}
                  onChange={(e) => setFormData({ ...formData, attendance_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Request Type *
                </label>
                <Select
                  value={formData.request_type}
                  onValueChange={(value) => setFormData({ ...formData, request_type: value })}
                  options={[
                    { value: 'correction', label: 'Correction' },
                    { value: 'manual_entry', label: 'Manual Entry' },
                    { value: 'leave_adjustment', label: 'Leave Adjustment' },
                    { value: 'missing_punch', label: 'Missing Punch' }
                  ]}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Clock In Time
                  </label>
                  <Input
                    type="time"
                    value={formData.requested_clock_in}
                    onChange={(e) => setFormData({ ...formData, requested_clock_in: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Clock Out Time
                  </label>
                  <Input
                    type="time"
                    value={formData.requested_clock_out}
                    onChange={(e) => setFormData({ ...formData, requested_clock_out: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Break Duration (minutes)
                </label>
                <Input
                  type="number"
                  value={formData.requested_break_duration_minutes}
                  onChange={(e) => setFormData({ ...formData, requested_break_duration_minutes: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Reason *
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows="4"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Explain why you need this regularization..."
                  required
                />
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateRequest} disabled={loading}>
                  {loading ? 'Creating...' : 'Create Request'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRegularization;

